import React, { FunctionComponent, useState } from 'react';

import _ from 'lodash';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { useProfile } from 'auth/Auth';
import { Overlay } from 'core/overlay/Overlay';
import { ButtonVariant } from 'core/util/Bootstrap';
import { AgeField } from 'social/publisher/post-target/post-target-fields/AgeField';
import { EducationalStatusField } from 'social/publisher/post-target/post-target-fields/EducationalStatusField';
import { FieldWrapper } from 'social/publisher/post-target/post-target-fields/FieldWrapper';
import { GenderField } from 'social/publisher/post-target/post-target-fields/GenderField';
import { InterestField } from 'social/publisher/post-target/post-target-fields/InterestField';
import { LanguageField } from 'social/publisher/post-target/post-target-fields/LanguageField';
import { LocationField } from 'social/publisher/post-target/post-target-fields/LocationField';
import { RelationshipStatusField } from 'social/publisher/post-target/post-target-fields/RelationshipStatusField';
import {
    AgeRange,
    Gender,
    FacebookTargetFields,
    EducationalStatus,
    RelationshipStatus,
    TargetInterest,
    FacebookLocale,
    FacebookGeoLocation,
} from 'social/publisher/Publisher.types';
import 'social/common/util/Util.scss';
import { usePublisher } from 'social/publisher/PublisherContext';
import { isFacebookTargetFieldsSameAsDefault } from 'social/publisher/util/Util';

import 'social/publisher/post-target/PostTarget.scss';

interface Props {
    onClose: any;
    onActive: any;
}

const OVERLAY_AREA = 'social-publisher-post-target';
const DEFAULT_TARGET_AUDIENCE: FacebookTargetFields = {
    interests: [],
    ages: { ageMin: 13, ageMax: 65 },
    genders: [],
    relationshipStatuses: [],
    educationalStatuses: [],
    geoLocations: {
        countries: [],
        regions: [],
        cities: [],
    },
    locales: [],
};

export const PostTarget: FunctionComponent<Props> = ({ onClose, onActive }) => {
    const { t } = useTranslation(['social/publisher']);
    const [{ profile }] = useProfile();
    const [{ selectedSources, targetAudience }, dispatch] = usePublisher();
    const initialTargetAudience = _.assign({}, DEFAULT_TARGET_AUDIENCE, targetAudience.FACEBOOK);
    const [currentTargetAudience, setCurrentTargetAudience] = useState(initialTargetAudience);
    // it's very laggy to save every change to the publisher state from inside of response editor, so we are saving all the changes once when user exits the modal
    const handleSave = (): void => {
        dispatch({ type: 'set-target-audience', payload: { value: { FACEBOOK: _.cloneDeep(currentTargetAudience) } } });

        const isFieldsSameAsDefault = isFacebookTargetFieldsSameAsDefault(currentTargetAudience);

        if (isFieldsSameAsDefault) {
            onActive(false);
        } else {
            onActive(true);
        }
        onClose();
    };
    const handleCancel = (): void => {
        onClose();
    };
    const saveInterests = (interests: TargetInterest[]): void => {
        const newTargetAudience = _.cloneDeep(currentTargetAudience);

        newTargetAudience.interests = interests;
        setCurrentTargetAudience(newTargetAudience);
    };
    const saveGender = (genders: Gender[]): void => {
        const newTargetAudience = _.cloneDeep(currentTargetAudience);

        newTargetAudience.genders = genders;
        setCurrentTargetAudience(newTargetAudience);
    };
    const saveAge = (ages: AgeRange): void => {
        const newTargetAudience = _.cloneDeep(currentTargetAudience);

        newTargetAudience.ages = ages;
        setCurrentTargetAudience(newTargetAudience);
    };
    const saveEducationalStatus = (educationalStatuses: EducationalStatus[]): void => {
        const newTargetAudience = _.cloneDeep(currentTargetAudience);

        newTargetAudience.educationalStatuses = educationalStatuses;
        setCurrentTargetAudience(newTargetAudience);
    };
    const saveRelationshipStatus = (relationshipStatuses: RelationshipStatus[]): void => {
        const newTargetAudience = _.cloneDeep(currentTargetAudience);

        newTargetAudience.relationshipStatuses = relationshipStatuses;
        setCurrentTargetAudience(newTargetAudience);
    };
    const saveLocales = (locales: FacebookLocale[]): void => {
        const newTargetAudience = _.cloneDeep(currentTargetAudience);

        newTargetAudience.locales = locales;
        setCurrentTargetAudience(newTargetAudience);
    };
    const saveGeoLocations = (geoLocations: FacebookGeoLocation): void => {
        const newTargetAudience = _.cloneDeep(currentTargetAudience);

        newTargetAudience.geoLocations = geoLocations;
        setCurrentTargetAudience(newTargetAudience);
    };
    const saveIsUnpublished = (isUnpublished: boolean): void => {
        const newTargetAudience = _.cloneDeep(currentTargetAudience);

        newTargetAudience.isUnpublished = isUnpublished;
        setCurrentTargetAudience(newTargetAudience);
    };

    return (
        <div className="r4e-social-post-target">
            <Overlay area={OVERLAY_AREA} />
            <div className="post-target-header">
                <div className="post-target-header-text">{t('FACEBOOK_TARGETING_OPTIONS')}</div>
            </div>
            <div className="post-target-body">
                {selectedSources.FACEBOOK ? (
                    <>
                        <div className={`${currentTargetAudience.isUnpublished && 'disabled-div'}`}>
                            <FieldWrapper fieldLabel="INTERESTS" className="interests-field">
                                <InterestField
                                    interests={currentTargetAudience.interests}
                                    tenantId={profile?.tenant?.id}
                                    save={saveInterests}
                                />
                            </FieldWrapper>
                            <FieldWrapper fieldLabel="AGE" className="age-field">
                                <AgeField ages={currentTargetAudience.ages} save={saveAge} />
                            </FieldWrapper>
                            <FieldWrapper fieldLabel="GENDER" className="gender-field">
                                <GenderField genders={currentTargetAudience.genders} save={saveGender} />
                            </FieldWrapper>
                            <FieldWrapper fieldLabel="LOCATION" className="location-field">
                                <LocationField
                                    geoLocations={currentTargetAudience.geoLocations}
                                    tenantId={profile?.tenant?.id}
                                    save={saveGeoLocations}
                                />
                            </FieldWrapper>
                            <FieldWrapper fieldLabel="LANGUAGE" className="language-field">
                                <LanguageField
                                    locales={currentTargetAudience.locales}
                                    tenantId={profile?.tenant?.id}
                                    save={saveLocales}
                                />
                            </FieldWrapper>
                            <FieldWrapper fieldLabel="EDUCATIONAL_STATUS" className="educational-status-field">
                                <EducationalStatusField
                                    educationalStatuses={currentTargetAudience.educationalStatuses}
                                    save={saveEducationalStatus}
                                />
                            </FieldWrapper>
                            <FieldWrapper fieldLabel="RELATIONSHIP_STATUS" className="relationship-status-field">
                                <RelationshipStatusField
                                    relationshipStatuses={currentTargetAudience.relationshipStatuses}
                                    save={saveRelationshipStatus}
                                />
                            </FieldWrapper>
                        </div>
                        <Form.Check
                            id="dark-post"
                            name="dark-post"
                            type="checkbox"
                            label={t('UNPUBLISHED_POST')}
                            defaultChecked={currentTargetAudience.isUnpublished}
                            className="top10"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                saveIsUnpublished(event.target.checked)
                            }
                        />
                    </>
                ) : (
                    <div className="post-target-body">{t('SELECT_FACEBOOK_TO_SEE_AUDIENCE_TARGETING_OPTIONS')}</div>
                )}
            </div>
            <div className="post-target-footer">
                {selectedSources.FACEBOOK && (
                    <>
                        <Button variant={ButtonVariant.Secondary} className="cancel-btn" onClick={handleCancel}>
                            {t('CANCEL')}
                        </Button>
                        <Button variant={ButtonVariant.Primary} className="save-btn" onClick={handleSave}>
                            {t('SAVE')}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};
